import {
  fetchUserPreferencesRow,
  resolveUserPreferencesScope,
  updateUserPreferences,
} from './userPreferencesApi'
import { createClerkSupabaseClientCommandCenter } from './utility'

export const SHIPMENT_FAVORITE_IDS_COLUMN = 'shipment_ids'
export const PRODUCT_RELEASE_FAVORITE_IDS_COLUMN = 'product_release_shipment_ids'

export function resolveFavoriteShipmentIdsColumn(listType) {
  return listType === 'temp-release'
    ? PRODUCT_RELEASE_FAVORITE_IDS_COLUMN
    : SHIPMENT_FAVORITE_IDS_COLUMN
}

export function resolveFavoriteShipmentIdsMap(listType, userPreferences) {
  const prefs = userPreferences || {}
  return listType === 'temp-release'
    ? prefs.productReleaseShipmentIds || {}
    : prefs.shipmentIds || {}
}

/** React/context keys → Supabase `user_preferences` column names */
const PREFERENCE_KEY_TO_DB_COLUMN = {
  mapStyle: 'map_style',
  customColumnWidth: 'custom_column_width',
}

export const preferenceKeyToDbColumn = (key) => PREFERENCE_KEY_TO_DB_COLUMN[key] ?? key

const mapPreferencePayloadToDbColumns = (prefs) =>
  Object.fromEntries(Object.entries(prefs).map(([k, v]) => [preferenceKeyToDbColumn(k), v]))

const resolveClerkId = (session, fallbackClerkId = null) => {
  return fallbackClerkId || session?.user?.id || session?.claims?.sub || session?.userId || null
}

/**
 * Fetches user-specific preferences from Supabase.
 * @param {object} session - Clerk session object.
 * @param {string | null} fallbackClerkId
 * @param {{ checkGate?: Function, orgId?: string | null }} scopeOptions
 * @returns {Promise<object|null>} - User preferences or null if not found/error.
 */
export const fetchUserPreferences = async (session, fallbackClerkId = null, scopeOptions = {}) => {
  if (!session) return null

  try {
    const client = createClerkSupabaseClientCommandCenter(session)
    const clerkId = resolveClerkId(session, fallbackClerkId)
    const { orgScoped, orgId } = resolveUserPreferencesScope({
      checkGate: scopeOptions.checkGate,
      orgId: scopeOptions.orgId,
    })

    const { data, error } = await fetchUserPreferencesRow({
      client,
      userId: clerkId,
      orgId,
      orgScoped,
      columns: '*',
    })

    if (error) {
      console.error('Error fetching user preferences from Supabase:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('Exception while fetching user preferences:', error)
    return null
  }
}

/**
 * Updates a single user preference in Supabase.
 * @param {object} session - Clerk session object.
 * @param {string} key - Preference key (e.g. 'timezone', 'mapStyle'); camelCase keys are mapped to DB columns.
 * @param {any} value - Preference value.
 * @param {string | null} fallbackClerkId
 * @param {{ checkGate?: Function, orgId?: string | null }} scopeOptions
 * @returns {Promise<boolean>} - Success or failure.
 */
export const updateUserPreference = async (
  session,
  key,
  value,
  fallbackClerkId = null,
  scopeOptions = {},
) => {
  if (!session || !key) return false

  try {
    const client = createClerkSupabaseClientCommandCenter(session)
    const clerkId = resolveClerkId(session, fallbackClerkId)

    if (!clerkId) {
      console.error(`Cannot update preference (${key}): missing clerkId`)
      return false
    }

    const dbKey = preferenceKeyToDbColumn(key)
    const { orgScoped, orgId } = resolveUserPreferencesScope({
      checkGate: scopeOptions.checkGate,
      orgId: scopeOptions.orgId,
    })

    const { data: updatedRows, error } = await updateUserPreferences({
      client,
      userId: clerkId,
      orgId,
      orgScoped,
      payload: { [dbKey]: value },
      selectColumns: 'user_id',
    })

    if (error) {
      console.error(`Error updating user preference (${dbKey}) in Supabase:`, error)
      return false
    }

    if (!updatedRows || updatedRows.length === 0) {
      const { error: legacyError } = await updateUserPreferences({
        client,
        userId: clerkId,
        orgId,
        orgScoped,
        payload: { [dbKey]: value },
      })

      if (legacyError) {
        console.error(`Error updating preference (${dbKey}) by legacy user_id:`, legacyError)
        return false
      }
    }

    return true
  } catch (error) {
    console.error(
      `Exception while updating user preference (${preferenceKeyToDbColumn(key)}):`,
      error,
    )
    return false
  }
}

/**
 * Updates multiple user preferences in Supabase.
 * @param {object} session - Clerk session object.
 * @param {object} preferences - Preferences object.
 * @param {string | null} fallbackClerkId
 * @param {{ checkGate?: Function, orgId?: string | null }} scopeOptions
 * @returns {Promise<boolean>} - Success or failure.
 */
export const updateMultipleUserPreferences = async (
  session,
  preferences,
  fallbackClerkId = null,
  scopeOptions = {},
) => {
  if (!session || !preferences) return false

  try {
    const client = createClerkSupabaseClientCommandCenter(session)
    const clerkId = resolveClerkId(session, fallbackClerkId)

    if (!clerkId) {
      console.error('Cannot update multiple preferences: missing clerkId')
      return false
    }

    const dbPayload = mapPreferencePayloadToDbColumns(preferences)
    const { orgScoped, orgId } = resolveUserPreferencesScope({
      checkGate: scopeOptions.checkGate,
      orgId: scopeOptions.orgId,
    })

    const { data: updatedRows, error } = await updateUserPreferences({
      client,
      userId: clerkId,
      orgId,
      orgScoped,
      payload: dbPayload,
      selectColumns: 'user_id',
    })

    if (error) {
      console.error('Error updating multiple user preferences in Supabase:', error)
      return false
    }

    if (!updatedRows || updatedRows.length === 0) {
      const { error: legacyError } = await updateUserPreferences({
        client,
        userId: clerkId,
        orgId,
        orgScoped,
        payload: dbPayload,
      })

      if (legacyError) {
        console.error('Error updating multiple preferences by legacy user_id:', legacyError)
        return false
      }
    }

    return true
  } catch (error) {
    console.error('Exception while updating multiple user preferences:', error)
    return false
  }
}

export {
  fetchUserPreferencesRow,
  insertUserPreferences,
  resolveUserPreferencesScope,
  selectUserPreferences,
  updateUserPreferences,
  verifyUserPreferencesRecord,
} from './userPreferencesApi'
